import { type FormEvent, useEffect, useState } from "react";
import { ApiError, type AppUser, type Role, api } from "./api";
import { SelectField } from "./fields";
import { PageHeader } from "./layout/Layout";
import { Actions, Alert, Badge, Box, Empty, Field, IconAction, Loading, Modal, Table, Th, TwoLine, Working } from "./ui";

/**
 * Logins, for a master (quotation A2).
 *
 * OHC asked to be able to add somebody themselves rather than ask us, which is fair:
 * people are taken on for a show and let go again. A new login gets a temporary password
 * shown once, to be handed over in person, and the person sets their own on first use,
 * so nobody, including whoever added them, knows their password.
 *
 * A login is switched off rather than deleted. What that person sold, counted and
 * changed stays theirs in the records.
 */

const ROLE_LABELS: Record<Role, string> = {
  sales_staff: "Sales staff",
  supervisor: "Supervisor",
  manager: "Manager",
  admin: "Administrator",
  master: "System administrator",
};

const ROLE_NOTES: Record<Role, string> = {
  sales_staff: "Process sales, record customer buybacks and count inventory.",
  supervisor:
    "Sales staff access, plus purchases, invoices, expense claims and cost information. Approve voids and corrections.",
  manager: "Supervisor access, plus reverse sales, adjust inventory, reconcile bank transactions and view reports.",
  admin: "Manager access, plus payment vouchers, record verification and financial accounts.",
  master: "Full access, including user access, devices, company details, expense-claim approval and period locking.",
};

export function Users(): React.ReactElement {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [people, setPeople] = useState<{ id: string; fullName: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [adding, setAdding] = useState(false);
  const [handOver, setHandOver] = useState<{ name: string; username: string; password: string } | undefined>();
  const [changing, setChanging] = useState<AppUser | undefined>();

  async function reload(): Promise<void> {
    try {
      const result = await api.users();
      setUsers(result.users);
      setRoles(result.roles);
      setPeople(result.peopleWithoutLogin);
      setError(undefined);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not load user access");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function reset(user: AppUser): Promise<void> {
    try {
      const result = await api.resetUserPassword(user.id);
      setHandOver({ name: user.fullName, username: user.username, password: result.temporaryPassword });
      await reload();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not reset the password");
    }
  }

  async function switchTo(user: AppUser, active: boolean): Promise<void> {
    try {
      await api.setUserActive(user.id, active);
      setError(undefined);
      await reload();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not update the user");
    }
  }

  if (loading) return <Loading />;

  const working = users.filter((user) => user.isActive);

  return (
    <>
      <PageHeader
        title="User access"
        subtitle="Manage users, passwords and permissions."
        actions={
          <button type="button" className="ti-btn ti-btn-primary-full" onClick={() => setAdding(true)}>
            <i className="ri-user-add-line" /> Add user
          </button>
        }
      />

      {error ? <Alert tone="danger">{error}</Alert> : null}

      <div className="grid grid-cols-12 gap-x-6">
        <div className="xl:col-span-8 col-span-12">
          <Box title="Users" count={working.length}>
            {users.length === 0 ? (
              <Empty icon="ri-user-line">No users have been added.</Empty>
            ) : (
              <Table
                head={
                  <>
                    <Th>User</Th>
                    <Th>Access role</Th>
                    <Th>Status</Th>
                    <Th end>Action</Th>
                  </>
                }
              >
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-defaultborder">
                    <td>
                      <div className="flex items-center">
                        <span className="avatar avatar-sm avatar-rounded bg-primary/10 text-primary font-semibold me-2 !mb-0">
                          {user.fullName
                            .split(/\s+/)
                            .map((word) => word.charAt(0))
                            .join("")
                            .slice(0, 2)
                            .toUpperCase()}
                        </span>
                        <TwoLine
                          main={user.fullName}
                          sub={`Username: ${user.username}${user.jobTitle ? ` · ${user.jobTitle}` : ""}`}
                        />
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0"
                        onClick={() => setChanging(user)}
                      >
                        {ROLE_LABELS[user.role]} <i className="ri-arrow-down-s-line" />
                      </button>
                    </td>
                    <td>
                      {!user.isActive ? (
                        <Badge tone="light">Deactivated</Badge>
                      ) : user.mustChangePassword ? (
                        <Badge tone="warning">Awaiting first sign-in</Badge>
                      ) : user.lockedUntil && new Date(user.lockedUntil) > new Date() ? (
                        <Badge tone="danger">Locked</Badge>
                      ) : (
                        <Badge tone="success">Active</Badge>
                      )}
                      {user.hasTwoFactor ? (
                        <span className="ms-1">
                          <Badge tone="info">Authenticator app</Badge>
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <Actions>
                        <IconAction
                          icon="ri-key-2-line"
                          label="New password"
                          tip="Reset password"
                          tone="primary"
                          onClick={() => void reset(user)}
                        />
                        {user.isActive ? (
                          <IconAction
                            icon="ri-shut-down-line"
                            label="Switch off"
                            tip="Deactivate user"
                            tone="danger"
                            onClick={() => void switchTo(user, false)}
                          />
                        ) : (
                          <IconAction
                            icon="ri-play-circle-line"
                            label="Switch back on"
                            tone="success"
                            onClick={() => void switchTo(user, true)}
                          />
                        )}
                      </Actions>
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </Box>
        </div>

        <div className="xl:col-span-4 col-span-12">
          <Box title="Role permissions">
            <ul className="list-none mb-0 p-0">
              {(roles.length > 0 ? roles : (Object.keys(ROLE_LABELS) as Role[])).map((role) => (
                <li key={role} className="mb-3 last:mb-0">
                  <p className="font-semibold text-[0.813rem] mb-1">{ROLE_LABELS[role]}</p>
                  <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-0">{ROLE_NOTES[role]}</p>
                </li>
              ))}
            </ul>
          </Box>
          <Box title="Passwords">
            <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50 mb-0">
              Adding a user or resetting a password gives a temporary password, shown once. Give it to the user in
              person or in a private message, never in a group chat. The user chooses a personal password at the
              next sign-in, so nobody else knows it. Any user can set up an authenticator app under Sign-in
              settings.
            </p>
          </Box>
        </div>
      </div>

      <AddLogin
        open={adding}
        roles={roles}
        people={people}
        onClose={() => setAdding(false)}
        onAdded={(name, username, password) => {
          setAdding(false);
          setHandOver({ name, username, password });
          void reload();
        }}
      />

      {changing ? (
        <ChangeRole
          user={changing}
          roles={roles}
          onClose={() => setChanging(undefined)}
          onDone={() => {
            setChanging(undefined);
            void reload();
          }}
          onProblem={setError}
        />
      ) : null}

      {handOver ? (
        <Modal
          open
          title={`${handOver.name} can now sign in`}
          onClose={() => setHandOver(undefined)}
          footer={
            <button type="button" className="ti-btn ti-btn-primary-full" onClick={() => setHandOver(undefined)}>
              Done
            </button>
          }
        >
          <div className="text-center py-2">
            <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50 mb-1">Username</p>
            <p className="font-semibold text-[1.25rem] mb-3">{handOver.username}</p>
            <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50 mb-1">Temporary password</p>
            <p className="font-semibold text-[1.5rem] tracking-[0.1em] text-primary mb-3">{handOver.password}</p>
            <p className="text-[0.813rem] mb-0">
              Shown once. Give it to the user in person or in a private message. They choose a personal
              password the first time they sign in.
            </p>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

/** A new login: a name, what they sign in as, and what they are allowed to do. */
function AddLogin({
  open,
  roles,
  people,
  onAdded,
  onClose,
}: {
  readonly open: boolean;
  readonly roles: readonly Role[];
  readonly people: readonly { id: string; fullName: string }[];
  readonly onAdded: (name: string, username: string, password: string) => void;
  readonly onClose: () => void;
}): React.ReactElement {
  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [role, setRole] = useState<Role>("sales_staff");
  const [personId, setPersonId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const chosen = people.find((person) => person.id === personId);

  async function submit(event?: FormEvent): Promise<void> {
    event?.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      const name = chosen ? chosen.fullName : fullName.trim();
      const result = await api.addUser({
        fullName: name,
        username: username.trim(),
        role,
        jobTitle: jobTitle.trim() || null,
        personId: personId || null,
      });
      setFullName("");
      setUsername("");
      setJobTitle("");
      setPersonId("");
      setRole("sales_staff");
      onAdded(name, result.user.username, result.temporaryPassword);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not add the user");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title="Add user"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-primary-full"
            disabled={busy || username.trim() === "" || (!chosen && fullName.trim().length < 2)}
            onClick={() => void submit()}
          >
            {busy ? <Working label="Adding" /> : "Add user"}
          </button>
        </>
      }
    >
      <form className="grid grid-cols-12 gap-4" onSubmit={(event) => void submit(event)}>
        {error ? (
          <div className="col-span-12">
            <Alert tone="danger">{error}</Alert>
          </div>
        ) : null}

        {people.length > 0 ? (
          <div className="col-span-12">
            <SelectField
              id="user-person"
              label="Somebody already on the system"
              value={personId}
              placeholder="New person"
              options={people.map((person) => ({ value: person.id, label: person.fullName }))}
              onChange={setPersonId}
            />
            <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-1 mb-0">
              Select a name to give sign-in access to someone who already holds inventory or is owed money.
              Otherwise, leave this blank and enter a new name.
            </p>
          </div>
        ) : null}

        {chosen ? null : (
          <Field id="user-name" label="Full name" className="sm:col-span-6 col-span-12">
            <input
              id="user-name"
              type="text"
              className="form-control"
              value={fullName}
              onChange={(changed) => setFullName(changed.target.value)}
            />
          </Field>
        )}

        <Field
          id="user-username"
          label="Username"
          hint="Letters and numbers only, no spaces"
          className="sm:col-span-6 col-span-12"
        >
          <input
            id="user-username"
            type="text"
            className="form-control"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={username}
            onChange={(changed) => setUsername(changed.target.value.toLowerCase())}
          />
        </Field>

        <Field id="user-job" label="Job title" className="sm:col-span-6 col-span-12">
          <input
            id="user-job"
            type="text"
            className="form-control"
            value={jobTitle}
            onChange={(changed) => setJobTitle(changed.target.value)}
          />
        </Field>

        <div className="sm:col-span-6 col-span-12">
          <SelectField
            id="user-role"
            label="Allowed to"
            value={role}
            options={(roles.length > 0 ? roles : (Object.keys(ROLE_LABELS) as Role[])).map((one) => ({
              value: one,
              label: ROLE_LABELS[one],
            }))}
            onChange={(value) => setRole(value as Role)}
          />
        </div>
        <div className="col-span-12">
          <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-0">{ROLE_NOTES[role]}</p>
        </div>
      </form>
    </Modal>
  );
}

/** Moving somebody up or down. */
function ChangeRole({
  user,
  roles,
  onClose,
  onDone,
  onProblem,
}: {
  readonly user: AppUser;
  readonly roles: readonly Role[];
  readonly onClose: () => void;
  readonly onDone: () => void;
  readonly onProblem: (message: string) => void;
}): React.ReactElement {
  const [role, setRole] = useState<Role>(user.role);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function submit(): Promise<void> {
    setError(undefined);
    setBusy(true);
    try {
      await api.setUserRole(user.id, role);
      onDone();
    } catch (problem) {
      const message = problem instanceof ApiError ? problem.message : "Could not change the access role";
      setError(message);
      onProblem(message);
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={`Access role for ${user.fullName}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="ti-btn ti-btn-primary-full" disabled={busy} onClick={() => void submit()}>
            {busy ? <Working label="Saving" /> : "Save"}
          </button>
        </>
      }
    >
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <SelectField
            id="user-new-role"
            label="Allowed to"
            value={role}
            options={(roles.length > 0 ? roles : (Object.keys(ROLE_LABELS) as Role[])).map((one) => ({
              value: one,
              label: ROLE_LABELS[one],
            }))}
            onChange={(value) => setRole(value as Role)}
          />
        </div>
        <div className="col-span-12">
          <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50 mb-0">{ROLE_NOTES[role]}</p>
        </div>
      </div>
    </Modal>
  );
}
